import { Injectable } from '@angular/core';
import {
    PartialCeLocation,
    GetLocationResponse,
    GetLocationStationsResponse,
    LocationsApiService,
    PatchLocationRequest,
    PostLocationRequest,
    GetLocationBillingSettingsResponse,
    PutLocationBillingSettingsRequest,
    PutLocationBillingSettingsResponse,
} from '@kempoweroyj/ngx-locations-api-client';
import { NotificationsService } from 'api/notifications.api';
import { Location } from 'api/ocpi.api';
import { from, merge, Observable, Subject } from 'rxjs';
import { filter, map, publishReplay, refCount, startWith, switchMap } from 'rxjs/operators';
import { retryWithDelay } from 'shared/utils/retryWithDelay';

export type CeLocationRow = GetLocationResponse['location'];

@Injectable({
    providedIn: 'root',
})
export class LocationsService {
    private refreshRequested$ = new Subject<void>();
    constructor(
        private api: LocationsApiService,
        private notifications: NotificationsService
    ) {}

    patchLocation(id: string, body: PatchLocationRequest) {
        return this.api.patchLocation(id, body);
    }

    deleteLocation(id: string) {
        return this.api.deleteLocation(id);
    }

    refresh() {
        this.refreshRequested$.next();
    }

    location$(id: string): Observable<CeLocationRow | undefined> {
        return from(
            this.api
                .getLocation(id)
                .then((res) => res.location)
                .catch(() => undefined as CeLocationRow)
        );
    }

    getLocation(id: string): Promise<CeLocationRow | undefined> {
        return this.location$(id).toPromise();
    }

    _locations$: Observable<CeLocationRow[]>;
    locations$(): Observable<CeLocationRow[]> {
        if (!this._locations$) {
            const refresh$ = merge(
                this.refreshRequested$,
                this.notifications.notification$.pipe(
                    filter((n) => n.type === 'ChargingStationUpdated' && n.changedSchemas?.includes('location'))
                )
            );
            // retryWithDelay self-heals transient API failures (e.g. token transition right after
            // impersonation switch); without it a single rejection silently stalls the pipeline.
            this._locations$ = refresh$.pipe(
                startWith([]),
                switchMap(() => this.api.getLocations()),
                map((res) => res?.locations ?? []),
                retryWithDelay<CeLocationRow[]>(),
                publishReplay(1),
                refCount()
            );
        }
        return this._locations$;
    }

    _locationStations$: { [locationId: string]: Observable<GetLocationStationsResponse['stations']> } = {};
    locationStations$(locationId: string): Observable<GetLocationStationsResponse['stations']> {
        if (!this._locationStations$[locationId]) {
            this._locationStations$[locationId] = this.refreshRequested$.pipe(
                startWith(0),
                switchMap(() => from(this.api.getLocationStations(locationId).then((ls) => ls.stations)))
            );
        }
        return this._locationStations$[locationId];
    }

    postLocation(body: PostLocationRequest) {
        return this.api.postLocation(body);
    }

    ocpiLocationToCeLocation(ol: Location): PartialCeLocation {
        return {
            id: undefined,
            name: ol.name,
            address: ol?.address,
            locationId: ol?.id,
            lat: +ol?.coordinates?.latitude || undefined,
            lng: +ol?.coordinates?.longitude || undefined,
        };
    }

    locationSettings$(locationUid: string) {
        return this.locationSettingsShouldBeRefreshed$(locationUid).pipe(
            startWith(null),
            switchMap(() => from(this.api.getLocationSettings(locationUid)))
        );
    }

    private _locationSettingsShouldBeRefreshed$ = new Subject<string>();
    notifyLocationSettingsShouldBeRefreshed(locationUid: string) {
        this._locationSettingsShouldBeRefreshed$.next(locationUid);
    }
    locationSettingsShouldBeRefreshed$(locationUid: string) {
        const settingsUpdated$ = this.notifications.notification$.pipe(
            filter(
                (n) =>
                    n.type === 'ChargingStationUpdated' &&
                    !n.stationId &&
                    n?.['locationUid'] === locationUid &&
                    n.changedSchemas?.includes('externalNotificationSettings')
            )
        );
        const up$ = merge(
            this._locationSettingsShouldBeRefreshed$.pipe(filter((_id) => _id === locationUid)),
            settingsUpdated$
        );
        return up$;
    }

    // _locationBillingSettings$: Observable<GetLocationBillingSettingsResponse>;
    locationBillingSettings$(locationUid: string): Observable<GetLocationBillingSettingsResponse> {
        return from(this.api.getLocationBillingSettings(locationUid));
    }

    async putLocationBillingSettings(
        locationUid: string,
        body: PutLocationBillingSettingsRequest
    ): Promise<PutLocationBillingSettingsResponse> {
        return await this.api.putLocationBillingSettings(locationUid, body);
        // Do we need to notify about the update using RxJS etc?
    }
}
